import api from './api';
import {
  StempelResponse,
  StempelStatus,
  Korrekturantrag,
  KorrekturantragRequest,
  ArbeitszeitAuswertung,
  ZeiterfassungEintrag,
  StempelEintragRequest,
  StempelEintragUpdateRequest,
  ArbeitszeitKonfiguration,
  ArbeitszeitKonfigurationRequest,
  Mitarbeiter,
} from '../types';

export const zeiterfassungService = {
  // Stempeln per QR-Deeplink – Token aus dem QR-Code + eingeloggter Mitarbeiter
  stempeln: async (token: string): Promise<StempelResponse> =>
    (await api.post(`/zeiterfassung/stempeln/${encodeURIComponent(token)}`)).data,

  // Aktueller Stempelstatus (Anzeige vor dem Stempeln)
  status: async (): Promise<StempelStatus> =>
    (await api.get('/zeiterfassung/status')).data,

  // Eigene Rohstempel (Detailansicht pro Tag)
  meineEintraege: async (von: string, bis: string): Promise<ZeiterfassungEintrag[]> =>
    (await api.get('/zeiterfassung/meine/eintraege', { params: { von, bis } })).data,

  // Korrekturanträge (Mitarbeiter)
  antragStellen: async (data: KorrekturantragRequest): Promise<Korrekturantrag> =>
    (await api.post('/zeiterfassung/korrekturantraege', data)).data,

  meineAntraege: async (von: string, bis: string): Promise<Korrekturantrag[]> =>
    (await api.get('/zeiterfassung/korrekturantraege/meine', { params: { von, bis } })).data,

  antragZurueckziehen: async (id: number): Promise<void> => {
    await api.delete(`/zeiterfassung/korrekturantraege/${id}`);
  },

  // Korrekturanträge (Admin)
  offeneAntraege: async (): Promise<Korrekturantrag[]> =>
    (await api.get('/zeiterfassung/korrekturantraege/offen')).data,

  antragGenehmigen: async (id: number): Promise<Korrekturantrag> =>
    (await api.put(`/zeiterfassung/korrekturantraege/${id}/genehmigen`)).data,

  antragAblehnen: async (id: number, grund?: string): Promise<Korrekturantrag> =>
    (await api.put(`/zeiterfassung/korrekturantraege/${id}/ablehnen`, { grund: grund ?? '' })).data,

  // Admin: geheimer Stempel-Token (für QR-Code) lesen / neu erzeugen
  getStempelToken: async (): Promise<{ token: string }> =>
    (await api.get('/zeiterfassung/stempel-token')).data,

  regenerateStempelToken: async (): Promise<{ token: string }> =>
    (await api.post('/zeiterfassung/stempel-token/regenerieren')).data,

  // Admin: IP-Beschränkung (nur Firmennetz)
  getStempelIp: async (): Promise<{ allowlist: string; meineIp: string }> =>
    (await api.get('/zeiterfassung/stempel-ip')).data,

  setStempelIp: async (allowlist: string): Promise<void> => {
    await api.put('/zeiterfassung/stempel-ip', { allowlist });
  },

  // Eigene Arbeitszeiten (Mitarbeiter-Selbstsicht)
  meine: async (von: string, bis: string): Promise<ArbeitszeitAuswertung> =>
    (await api.get('/zeiterfassung/meine', { params: { von, bis } })).data,

  // Admin: Auswertung über alle/einzelne Mitarbeiter
  auswertung: async (von: string, bis: string, userId?: number): Promise<ArbeitszeitAuswertung[]> =>
    (await api.get('/zeiterfassung/auswertung', { params: { von, bis, ...(userId ? { userId } : {}) } })).data,

  // Admin: Arbeitszeitnachweis als PDF herunterladen
  auswertungPdf: async (von: string, bis: string, userId?: number): Promise<void> => {
    const response = await api.get('/zeiterfassung/auswertung/pdf', {
      params: { von, bis, ...(userId ? { userId } : {}) },
      responseType: 'blob',
    });
    const cd = response.headers['content-disposition'];
    let filename = `Arbeitszeitnachweis_${von}_${bis}.pdf`;
    const match = cd?.match(/filename[^;=\n]*=([^;\n]*)/);
    if (match?.[1]) filename = match[1].replace(/['"]/g, '').trim();
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  // Admin: einzelne Stempel (für Korrekturen)
  eintraege: async (von: string, bis: string, userId?: number): Promise<ZeiterfassungEintrag[]> =>
    (await api.get('/zeiterfassung/eintraege', { params: { von, bis, ...(userId ? { userId } : {}) } })).data,

  addEintrag: async (data: StempelEintragRequest): Promise<ZeiterfassungEintrag> =>
    (await api.post('/zeiterfassung/eintraege', data)).data,

  updateEintrag: async (id: number, data: StempelEintragUpdateRequest): Promise<void> => {
    await api.put(`/zeiterfassung/eintraege/${id}`, data);
  },

  deleteEintrag: async (id: number): Promise<void> => {
    await api.delete(`/zeiterfassung/eintraege/${id}`);
  },

  // Admin: aktive Mitarbeiter (ohne ausgeblendete) – für Filter-Dropdowns
  getAktiveMitarbeiter: async (): Promise<Mitarbeiter[]> =>
    (await api.get('/arbeitszeit-konfiguration/mitarbeiter')).data,

  // Admin: Soll-Konfiguration
  getKonfigurationen: async (): Promise<ArbeitszeitKonfiguration[]> =>
    (await api.get('/arbeitszeit-konfiguration')).data,

  setKonfiguration: async (userId: number, data: ArbeitszeitKonfigurationRequest): Promise<void> => {
    await api.put(`/arbeitszeit-konfiguration/${userId}`, data);
  },

  // Admin: Benutzer in der Personal-Übersicht aus-/einblenden
  setKonfigurationAusgeblendet: async (userId: number, ausgeblendet: boolean): Promise<void> => {
    await api.put(`/arbeitszeit-konfiguration/${userId}/ausgeblendet`, { ausgeblendet });
  },
};

export default zeiterfassungService;
